<script setup lang="ts">
import { type Attachment } from '@/composables/useFileAttachments';
import { readableFileSize } from '@/utils/functions/readableFileSize';
import { useFileTypeIcon } from '@/composables/useFileTypeIcon';
import { computed } from 'vue';
import { useDownloadFile } from '@/composables/useDownloadFile';
import { useI18n } from 'vue-i18n';
import { POSITION, useToast } from 'vue-toastification';

const props = defineProps<{
  attachment: Attachment;
}>();

const { t } = useI18n();
const toast = useToast();

const { fileIcon } = useFileTypeIcon(props.attachment.mimeType);

const { handleDownload, isDownloading } = useDownloadFile({
  fileSource: props.attachment.source,
  fileName: props.attachment.title,
});

async function onDownload() {
  try {
    await handleDownload();
  } catch (err) {
    console.error('Download failed:', err);
    toast.error(`${t('fileAttachments.errorDownload')} (${err.message})`, {
      position: POSITION.BOTTOM_RIGHT,
      timeout: 5000,
    });
  }
}

const readableDate = computed(() => {
  const dateObj = new Date(props.attachment.audit.createdAtUtc);
  return dateObj.toLocaleString();
});
</script>

<template>
  <div
    class="grid grid-cols-[1fr_auto_auto] items-center gap-4 w-full max-w-full relative"
  >
    <div class="flex items-center gap-2 min-w-0">
      <div class="w-4 h-4 flex items-center justify-start">
        <font-awesome-icon :icon="fileIcon" />
      </div>
      <span class="truncate p-0.5" :title="attachment.title">
        {{ attachment.title }}
      </span>
    </div>
    <div v-tippy="readableDate">
      <span class="text-gray-500">
        {{ readableFileSize(attachment.fileSize) }}
      </span>
    </div>

    <div class="flex items-center justify-end gap-2 relative">
      <button
        v-tippy="t('fileAttachments.downloadAttachment')"
        :class="{
          'text-neutral-500': isDownloading,
          'text-gray-500 hover:text-gray-800': !isDownloading,
          'animate-spin': isDownloading,
        }"
        :disabled="isDownloading"
        @click="onDownload"
      >
        <font-awesome-icon
          :icon="isDownloading ? ['far', 'spinner'] : ['fas', 'download']"
        />
      </button>
    </div>
  </div>
</template>
